// =============================================================================
// VoiceTextarea: a textarea with a built-in mic button (project CLAUDE.md rule
// #2 — "Every textarea has a mic icon"). Themed equivalent of apps/web/src/
// components/corpus/VoiceTextarea.tsx (same useDictation-backed dictation
// behaviour) restyled for Throughline: CSS Modules + --tl-* theme vars instead
// of Tailwind utility classes, same separation Orb.tsx established for B-2 —
// see that file's header for why every Throughline component takes this route
// rather than importing apps/web's Tailwind-classed version directly.
// =============================================================================

import { useId } from 'react';
import { useDictation } from '../lib/useDictation';
import clsx from './clsx';
import styles from './VoiceTextarea.module.css';

export interface VoiceTextareaProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  hint?: string;
  disabled?: boolean;
  /** E7-WALK-WAVE W4 (2026-07-17) — an optional extra class merged onto the
   *  <textarea> itself (never the outer .field wrapper), additive alongside
   *  .textarea rather than replacing it. Undefined by default, so every
   *  existing call site renders byte-identically; ImportPasteCard's finale
   *  usage is the first caller, opting the paste target into a visibly
   *  larger min-height on mobile. */
  textareaClassName?: string;
  /** W22 (2026-07-24) — an optional post-pass applied to each FINAL dictation
   *  chunk BEFORE it is appended to the value. The gap sheet passes its term
   *  repair here (lib/termRepair.ts) so "multi Khloud" never reaches the box
   *  in the first place. Undefined by default, so every existing call site
   *  behaves byte-identically — same additive discipline as
   *  `textareaClassName` above. Typed as a plain string transform on purpose:
   *  this component stays dumb about WHAT the repair is; the caller reports
   *  its own audit list. Never applied to TYPED text — only to what the
   *  microphone produced. */
  onFinalChunkTransform?: (chunk: string) => string;
}

export function VoiceTextarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 6,
  hint,
  disabled = false,
  textareaClassName,
  onFinalChunkTransform,
}: VoiceTextareaProps) {
  const id = useId();
  const dictation = useDictation({
    onFinalChunk: (rawChunk) => {
      const chunk = onFinalChunkTransform ? onFinalChunkTransform(rawChunk) : rawChunk;
      onChange((value ? `${value.replace(/\s+$/, '')} ${chunk}` : chunk).trim());
    },
  });

  const recording = dictation.state === 'recording';
  const live = recording && dictation.interim ? `${value}${value && !value.endsWith(' ') ? ' ' : ''}${dictation.interim}` : value;

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </div>
      <div className={styles.inputWrap}>
        <textarea
          id={id}
          value={live}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled || recording}
          className={clsx(styles.textarea, textareaClassName)}
        />
        {dictation.supported ? (
          <button
            type="button"
            onClick={() => (recording ? dictation.stop() : dictation.start())}
            aria-label={recording ? 'Stop dictation' : 'Start dictation'}
            aria-pressed={recording}
            disabled={disabled}
            className={clsx(styles.micButton, recording && styles.micButtonRecording)}
          >
            {recording ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="5" y="5" width="14" height="14" rx="2.5" fill="currentColor" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect
                  x="9"
                  y="3"
                  width="6"
                  height="11"
                  rx="3"
                  stroke="currentColor"
                  strokeWidth="1.9"
                />
                <path
                  d="M5 11a7 7 0 0 0 14 0M12 18v3"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}
